import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "@remix-run/react";
import { AlgoliaSearchPayload } from "@made-people/grease-algolia";
import { ProductList } from "@made-people/blueprint-product-list";
import { ButtonAsButton } from "@made-people/blueprint-button";
import { MissMaryAlgoliaSearchResultsProvider } from "../algolia-search-results-provider/mm-algolia-search-results-provider";
import { ChevronLeft } from "../../../assets/icons/ChevronLeft";
import { ChevronRight } from "../../../assets/icons/ChevronRight";

const CATEGORY_QUERY_PARAM = "category";

export interface ProductListCategoriesCollectionItem {
  _uid: string;
  label: string;
  centraCategory: string;
  default?: boolean;
  component?: string;
}

export interface ProductListCategoriesCollectionProps {
  productListCategories: ProductListCategoriesCollectionItem[];
}

const isSameCategory = (
  a: ProductListCategoriesCollectionItem | undefined,
  b: ProductListCategoriesCollectionItem | undefined,
): boolean =>
  !!a && !!b && a.label === b.label && a.centraCategory === b.centraCategory;

const findByCentraCategory = (
  items: ProductListCategoriesCollectionItem[],
  centraCategory: string | null,
): ProductListCategoriesCollectionItem | undefined =>
  centraCategory
    ? items.find((item) => item.centraCategory === centraCategory)
    : undefined;

const findDefault = (
  items: ProductListCategoriesCollectionItem[],
): ProductListCategoriesCollectionItem | undefined =>
  items.find((item) => item.default);

export const ProductListCategoriesCollection = ({
  productListCategories,
}: ProductListCategoriesCollectionProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const buttonsContainerRef = useRef<HTMLDivElement>(null);

  const [isOnStart, setIsOnStart] = useState(true);
  const [isOnEnd, setIsOnEnd] = useState(true);
  const [canScroll, setCanScroll] = useState(false);

  const items = useMemo(
    () => (Array.isArray(productListCategories) ? productListCategories : []),
    [productListCategories],
  );

  const defaultCategory = useMemo(() => findDefault(items), [items]);

  const selectedCategory = useMemo(() => {
    const fromQuery = findByCentraCategory(
      items,
      searchParams.get(CATEGORY_QUERY_PARAM),
    );
    if (fromQuery) {
      return fromQuery;
    }
    return defaultCategory;
  }, [items, defaultCategory, searchParams]);

  const algoliaPayload = useMemo<AlgoliaSearchPayload | undefined>(() => {
    if (!selectedCategory?.centraCategory) {
      return undefined;
    }
    return {
      algoliaTypeId: "product",
      searchQuery: "",
      disjunctiveFacets: ["*"],
      algoliaRequestOptions: {
        facetFilters: [`categories.uri:${selectedCategory.centraCategory}`],
      },
    };
  }, [selectedCategory]);

  const writeCategoryToUrl = useCallback(
    (centraCategory: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set(CATEGORY_QUERY_PARAM, centraCategory);
          return next;
        },
        { replace: true, preventScrollReset: true },
      );
    },
    [setSearchParams],
  );

  const clearCategoryFromUrl = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(CATEGORY_QUERY_PARAM);
        return next;
      },
      { replace: true, preventScrollReset: true },
    );
  }, [setSearchParams]);

  useEffect(() => {
    if (!selectedCategory) {
      return;
    }
    if (
      searchParams.get(CATEGORY_QUERY_PARAM) === selectedCategory.centraCategory
    ) {
      return;
    }
    writeCategoryToUrl(selectedCategory.centraCategory);
  }, [selectedCategory, searchParams, writeCategoryToUrl]);

  const onCategorySelected = useCallback(
    (category: ProductListCategoriesCollectionItem) => {
      if (
        isSameCategory(defaultCategory, category) &&
        isSameCategory(selectedCategory, category)
      ) {
        return;
      }

      if (isSameCategory(selectedCategory, category)) {
        if (defaultCategory) {
          writeCategoryToUrl(defaultCategory.centraCategory);
        } else {
          clearCategoryFromUrl();
        }
        return;
      }

      writeCategoryToUrl(category.centraCategory);
    },
    [
      defaultCategory,
      selectedCategory,
      writeCategoryToUrl,
      clearCategoryFromUrl,
    ],
  );

  const recalculate = useCallback(() => {
    const container = buttonsContainerRef.current;
    if (!container) {
      return;
    }

    const { scrollLeft, clientWidth, scrollWidth } = container;
    setCanScroll(scrollWidth > clientWidth + 1);

    const paddingLeft =
      parseFloat(window.getComputedStyle(container).paddingLeft) || 0;
    const threshold = paddingLeft + 20;

    setIsOnStart(scrollLeft <= threshold);
    setIsOnEnd(Math.round(scrollLeft) + clientWidth >= scrollWidth - threshold);
  }, []);

  useEffect(() => {
    recalculate();

    const container = buttonsContainerRef.current;
    window.addEventListener("resize", recalculate);

    let resizeObserver: ResizeObserver | undefined;
    if (container && typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(() => recalculate());
      resizeObserver.observe(container);
    }

    return () => {
      window.removeEventListener("resize", recalculate);
      resizeObserver?.disconnect();
    };
  }, [recalculate, items.length]);

  const scrollByOneViewport = (direction: -1 | 1) => {
    const container = buttonsContainerRef.current;
    if (!container) {
      return;
    }
    const { scrollLeft, clientWidth, scrollWidth } = container;
    const target =
      direction === 1
        ? Math.min(scrollLeft + clientWidth, scrollWidth - clientWidth)
        : Math.max(scrollLeft - clientWidth, 0);
    container.scrollTo({ left: target, behavior: "smooth" });
  };

  if (items.length === 0) {
    return null;
  }

  const pillBaseClasses =
    "flex h-12 shrink-0 cursor-pointer items-center justify-center whitespace-nowrap rounded border border-beige-200 px-8 py-3 text-body-md transition-colors";

  return (
    <>
      <div className="relative mb-10 w-full px-6 py-2 lg:px-16 lg:pt-10">
        {canScroll && !isOnStart && (
          <button
            type="button"
            onClick={() => scrollByOneViewport(-1)}
            aria-label="Scroll categories left"
            className="absolute left-2 top-1/2 z-10 flex size-[30px] -translate-y-1/2 items-center justify-center rounded-full bg-beige-100 lg:size-[50px]"
          >
            <ChevronLeft className="size-3 lg:size-4" />
          </button>
        )}

        <div
          ref={buttonsContainerRef}
          onScroll={recalculate}
          className={`flex w-full snap-x snap-mandatory flex-row gap-2 overflow-x-auto scrollbar-hide lg:snap-none ${
            canScroll ? "" : "justify-center overflow-x-hidden"
          }`}
        >
          {items.map((category, index) => (
            <ButtonAsButton
              key={category._uid ?? category.centraCategory + index}
              type="button"
              onClick={() => onCategorySelected(category)}
              label={category.label}
              variants={{ color: "transparent" }}
              className={`${pillBaseClasses} snap-start ${
                isSameCategory(selectedCategory, category)
                  ? "bg-black text-white border-black font-medium"
                  : ""
              }`}
            />
          ))}
        </div>

        {canScroll && !isOnEnd && (
          <button
            type="button"
            onClick={() => scrollByOneViewport(1)}
            aria-label="Scroll categories right"
            className="absolute right-2 top-1/2 z-10 flex size-[30px] -translate-y-1/2 items-center justify-center rounded-full bg-beige-100 lg:size-[50px]"
          >
            <ChevronRight className="size-3 lg:size-4" />
          </button>
        )}
      </div>

      {algoliaPayload && (
        <MissMaryAlgoliaSearchResultsProvider payload={algoliaPayload}>
          <ProductList />
        </MissMaryAlgoliaSearchResultsProvider>
      )}
    </>
  );
};
